// Pure functions over runs for the workload pages: the status filters of the runs list, a
// workflow's last-30-days figures, and the steps of a run's status timeline.
import type { Run } from "../api/client";
import { messages } from "../i18n";
import type { TimelineStep } from "../ui/Timeline";
import { clockTime } from "./format";

export type RunFilter = "all" | "waiting" | "running" | "succeeded" | "failed";

const FILTER_STATUSES: Record<Exclude<RunFilter, "all">, ReadonlySet<string>> = {
  waiting: new Set(["waiting_for_human"]),
  running: new Set(["queued", "admitted", "starting", "running", "collecting"]),
  succeeded: new Set(["succeeded"]),
  failed: new Set(["failed", "timed_out"]),
};

export const RUN_FILTERS: RunFilter[] = ["all", "waiting", "running", "succeeded", "failed"];

export function parseFilter(value: string | null): RunFilter {
  return RUN_FILTERS.find((f) => f === value) ?? "all";
}

export function matchesFilter(run: Run, filter: RunFilter): boolean {
  return filter === "all" || FILTER_STATUSES[filter].has(run.status);
}

export function filterCounts(runs: Run[]): Record<RunFilter, number> {
  const counts = { all: runs.length, waiting: 0, running: 0, succeeded: 0, failed: 0 };
  for (const r of runs) {
    for (const key of ["waiting", "running", "succeeded", "failed"] as const) {
      if (FILTER_STATUSES[key].has(r.status)) counts[key] += 1;
    }
  }
  return counts;
}

export function newestFirst(runs: Run[]): Run[] {
  return [...runs].sort((a, b) => b.queued_at - a.queued_at);
}

const DAY = 86_400_000;
const FINISHED_OK = new Set(["succeeded"]);
const FINISHED_BAD = new Set(["failed", "timed_out"]);

export interface WorkflowStats {
  runs: number;
  /** Of the runs that finished succeeded or failed; undefined when none did. */
  successPct: number | undefined;
  avgCost: number | undefined;
  lastRunAt: number | undefined;
}

export function workflowStats(runs: Run[], now = Date.now()): WorkflowStats {
  const recent = runs.filter((r) => now - r.queued_at < 30 * DAY);
  const ok = recent.filter((r) => FINISHED_OK.has(r.status)).length;
  const bad = recent.filter((r) => FINISHED_BAD.has(r.status)).length;
  const costs = recent.map((r) => r.cost_usd).filter((c): c is number => c != null);
  return {
    runs: recent.length,
    successPct: ok + bad === 0 ? undefined : Math.round((ok / (ok + bad)) * 100),
    avgCost: costs.length === 0 ? undefined : costs.reduce((a, b) => a + b, 0) / costs.length,
    lastRunAt: runs.reduce<number | undefined>((m, r) => (m === undefined || r.queued_at > m ? r.queued_at : m), undefined),
  };
}


const BEFORE_START = new Set(["queued", "admitted", "starting"]);

/**
 * A run's life as timeline steps, from its timestamps and its current status (which may be
 * the relay's newer one): queued, worker started, agent running, asked you (only while
 * waiting), then how it ended.
 */
export function runTimeline(run: Run, status: string): TimelineStep[] {
  const t = messages().workloads.run.timeline;
  const ended = (t.ended as Partial<Record<string, string>>)[status];
  const at = (ms: number | null | undefined) => (ms == null ? undefined : clockTime(ms));
  const started = run.started_at != null || !BEFORE_START.has(status);
  const steps: TimelineStep[] = [
    { key: "queued", label: t.queued, state: "done", at: at(run.queued_at), detail: t.queuedDetail },
    {
      key: "worker",
      label: t.workerStarted,
      state: started ? "done" : status === "queued" ? "todo" : "current",
      at: at(run.started_at),
      detail: t.workerDetail,
    },
    {
      key: "agent",
      label: t.agentRunning,
      state: ended || status === "waiting_for_human" ? "done" : started ? "current" : "todo",
      detail: t.agentDetail,
    },
  ];
  if (status === "waiting_for_human") {
    steps.push({ key: "asked", label: t.askedYou, state: "current", detail: t.askedDetail });
  }
  steps.push({
    key: "end",
    label: ended ?? t.completed,
    state: ended ? "done" : "todo",
    at: at(run.finished_at),
    detail: ended ? run.status_reason ?? t.endedDetail : t.pendingDetail,
  });
  if (status === "queued") steps[0] = { ...steps[0]!, state: "current" };
  return steps;
}
